'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';

import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { Card } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useGetSystemSitesQuery } from 'src/api/redux';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import { getProfileUrl } from 'src/utils/profileUrl';

const MAX_ITEMS = 5;

const CompactSitesBySpecialty: React.FC = () => {
  const { entityDetails } = useProfile();
  const { closeDrawer } = useDrawer();
  const widgetConfig = useWidgetConfig();

  const { providerId: systemId } = getEntityIdentifiers(entityDetails);
  const profileType = entityDetails?.type || 'system';

  const volumeFilterSetId = widgetConfig?.filterSet?.id;
  const volumeKey = volumeFilterSetId ? String(volumeFilterSetId) : null;
  const widgetFilterSetId = widgetConfig?.filterSetId;

  const codeSetLabel = widgetConfig?.filterSet?.label
    ? `${widgetConfig.filterSet.label} Claims`
    : 'All Claims';

  const queryArgs = useMemo(() => {
    const args: Record<string, any> = {
      systemId,
      page: 1,
      pageSize: MAX_ITEMS,
    };

    if (volumeKey) {
      args.sort = 'volume';
      args.sort_id = volumeKey;
      args.sort_field = 'patients';
      args.order = 'desc';
    } else {
      args.sort = 'name';
      args.order = 'asc';
    }

    if (widgetFilterSetId) {
      args.filterSets = [widgetFilterSetId];
    }

    if (volumeKey) {
      args.data = {
        [volumeKey]: {
          type: 'patients',
          date_range: { gte: widgetConfig?.minYear, lte: widgetConfig?.maxYear },
          filter_sets: [],
          filters: [],
        },
      };
    } else {
      args.data = {};
    }

    return args;
  }, [systemId, volumeKey, widgetFilterSetId, widgetConfig?.minYear, widgetConfig?.maxYear]);

  const { data, isLoading } = useGetSystemSitesQuery(queryArgs, {
    skip: !systemId,
  });

  const rows = data?.results || [];
  const totalCount = data?.count || 0;

  return (
    <Card
      className="p-4 flex flex-col gap-2 bg-white h-full"
      data-testid="compact-sites-by-specialty"
    >
      <WidgetHeader fallbackTitle={`Sites - ${codeSetLabel}`} />

      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading || widgetConfig?.isLoading ? (
          <WidgetLoading data-testid="compact-sites-by-specialty-loading" />
        ) : rows.length === 0 ? (
          <WidgetEmpty data-testid="compact-sites-by-specialty-empty" variant="full" />
        ) : (
          <div
            className="flex flex-col"
            role="list"
            aria-label="Sites"
            data-testid="compact-sites-by-specialty-list"
          >
            <div className="flex items-center justify-end gap-2 px-1 pb-1 border-b border-neutral-100">
              <span className="text-xs font-semibold text-teal-700 min-w-16 text-end">
                Patients
              </span>
              <span className="text-xs font-semibold text-teal-700 min-w-16 text-end">
                Claims
              </span>
            </div>
            {rows.map((site: Record<string, any>) => (
              <div
                key={site.provider_id}
                className="flex items-center justify-between gap-2 py-2 border-b border-neutral-100 last:border-b-0"
                role="listitem"
              >
                <div className="flex flex-col min-w-0 flex-1">
                  <Link
                    href={getProfileUrl('clinic', site.provider_id)}
                    onClick={() => closeDrawer('profile')}
                    className="text-sm font-bold text-link truncate hover:underline"
                  >
                    {site.name || 'N/A'}
                  </Link>
                  {site.specialty && (
                    <span className="text-xs text-secondary truncate">
                      {site.specialty}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="flex items-center justify-end bg-teal-50 rounded px-2 py-1 min-w-16">
                    <span className="text-sm font-semibold text-teal-900 tabular-nums">
                      {volumeKey && site.patients?.[volumeKey] != null
                        ? Number(site.patients[volumeKey]).toLocaleString()
                        : '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-end bg-teal-50 rounded px-2 py-1 min-w-16">
                    <span className="text-sm font-semibold text-teal-900 tabular-nums">
                      {volumeKey && site.claims?.[volumeKey] != null
                        ? Number(site.claims[volumeKey]).toLocaleString()
                        : '-'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {totalCount > 0 && (
        <Link
          href={getProfileUrl(profileType, systemId, 'system-sites')}
          onClick={() => closeDrawer('profile')}
          className="flex items-center gap-1.5 text-sm font-medium text-sky-600 hover:text-sky-700 transition-colors"
          data-testid="compact-sites-by-specialty-view-all"
        >
          View all {Number(totalCount).toLocaleString()} sites
          <Icon icon={faArrowRight} className="h-3 w-3" />
        </Link>
      )}
    </Card>
  );
};

export default CompactSitesBySpecialty;
